import { useState, useEffect } from "react";
import { Download, Database, Calendar, FileSpreadsheet, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { useBackupExport, type BackupCategory } from "@/hooks/useBackupExport";
import { format, subDays, startOfYear, endOfDay, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

type PeriodOption = "30" | "60" | "90" | "year" | "custom";

const categoryLabels: Record<BackupCategory, string> = {
  orders: "Pedidos e vendas",
  cash: "Movimentações de caixa",
  expenses: "Despesas",
  products: "Produtos e ingredientes",
  customers: "Clientes e avaliações",
  logs: "Logs do sistema",
};

export function BackupCard() {
  const { isExporting, progress, progressMessage, estimatedRecords, exportBackup, estimateRecords } = useBackupExport();

  const [period, setPeriod] = useState<PeriodOption>("30");
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(subDays(new Date(), 30));
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(new Date());
  const [selectedCategories, setSelectedCategories] = useState<BackupCategory[]>([
    "orders",
    "cash",
    "expenses",
    "products",
    "customers",
  ]);

  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();
    switch (period) {
      case "30":
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
      case "60":
        return { start: startOfDay(subDays(now, 60)), end: endOfDay(now) };
      case "90":
        return { start: startOfDay(subDays(now, 90)), end: endOfDay(now) };
      case "year":
        return { start: startOfYear(now), end: endOfDay(now) };
      case "custom":
        return {
          start: customStartDate ? startOfDay(customStartDate) : startOfDay(subDays(now, 30)),
          end: customEndDate ? endOfDay(customEndDate) : endOfDay(now),
        };
      default:
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
    }
  };

  const toggleCategory = (category: BackupCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const handleExport = () => {
    if (selectedCategories.length === 0) return;
    const { start, end } = getDateRange();
    exportBackup(selectedCategories, start, end);
  };

  // Estimate records when categories or period changes
  useEffect(() => {
    if (selectedCategories.length > 0) {
      const { start, end } = getDateRange();
      estimateRecords(selectedCategories, start, end);
    }
  }, [selectedCategories, period, customStartDate, customEndDate]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Database className="h-5 w-5" />
          Backup de Dados
        </CardTitle>
        <CardDescription>
          Exporte todos os dados operacionais em um arquivo Excel
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Period Selector */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Período
          </Label>
          <Select value={period} onValueChange={(v) => setPeriod(v as PeriodOption)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="60">Últimos 60 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
              <SelectItem value="year">Ano atual</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>

          {/* Custom Date Range */}
          {period === "custom" && (
            <div className="flex gap-2 mt-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                    <Calendar className="mr-2 h-4 w-4" />
                    {customStartDate ? format(customStartDate, "dd/MM/yyyy", { locale: ptBR }) : "Início"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={customStartDate}
                    onSelect={setCustomStartDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                    <Calendar className="mr-2 h-4 w-4" />
                    {customEndDate ? format(customEndDate, "dd/MM/yyyy", { locale: ptBR }) : "Fim"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={customEndDate}
                    onSelect={setCustomEndDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Category Selection */}
        <div className="space-y-3">
          <Label>Dados a exportar</Label>
          <div className="space-y-2">
            {(Object.keys(categoryLabels) as BackupCategory[]).map((category) => (
              <div key={category} className="flex items-center space-x-2">
                <Checkbox
                  id={category}
                  checked={selectedCategories.includes(category)}
                  onCheckedChange={() => toggleCategory(category)}
                  disabled={isExporting}
                />
                <Label htmlFor={category} className="text-sm font-normal cursor-pointer">
                  {categoryLabels[category]}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Estimated Records */}
        {estimatedRecords !== null && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Estimativa: ~{estimatedRecords.toLocaleString("pt-BR")} registros
          </div>
        )}

        {/* Progress Bar */}
        {isExporting && (
          <div className="space-y-2">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {progressMessage}
            </p>
          </div>
        )}

        {/* Export Button */}
        <Button
          onClick={handleExport}
          disabled={isExporting || selectedCategories.length === 0}
          className="w-full"
        >
          {isExporting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Exportando...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Exportar Backup
            </>
          )}
        </Button>

        {/* Info Note */}
        <p className="text-xs text-muted-foreground">
          ⚠️ Para backups automáticos diários, o Supabase Pro já mantém cópias de segurança do banco de dados.
        </p>
      </CardContent>
    </Card>
  );
}
